import * as React from 'react';
import { CalendarIcon, XIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { formatDate } from '@/lib/utils/format';

export interface DateRange {
  from?: Date;
  to?: Date;
}

interface DateRangePickerProps {
  from?: Date;
  to?: Date;
  onChange: (range: DateRange) => void;
  shortcuts?: boolean;
  className?: string;
}

export function DateRangePicker({
  from,
  to,
  onChange,
  shortcuts = true,
  className,
}: DateRangePickerProps) {
  const { t } = useTranslation('common');
  const [open, setOpen] = React.useState(false);

  const hasValue = from || to;

  const displayText = React.useMemo(() => {
    if (from && to) {
      return `${formatDate(from)} – ${formatDate(to)}`;
    }
    if (from) return `${t('dateRange.from', 'Ab')} ${formatDate(from)}`;
    if (to) return `${t('dateRange.to', 'Bis')} ${formatDate(to)}`;
    return t('dateRange.placeholder', 'Zeitraum wählen...');
  }, [from, to, t]);

  const handleSelect = React.useCallback(
    (range: { from?: Date; to?: Date } | undefined) => {
      onChange({ from: range?.from, to: range?.to });
    },
    [onChange],
  );

  const handleClear = React.useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onChange({ from: undefined, to: undefined });
    },
    [onChange],
  );

  const handleShortcut = React.useCallback(
    (getRange: () => DateRange) => {
      onChange(getRange());
      setOpen(false);
    },
    [onChange],
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      {/* Spec 514: der Clear-X war ein onClick auf dem 16px-SVG IM Trigger-Button
          (kein Button-in-Button moeglich, invalides HTML) → eigener, absolut
          positionierter Geschwister-Button (Salt-Field-Muster): echte Semantik,
          Tastatur, volle Hoehe, coarse ≥44 breit. */}
      <div className={cn('relative inline-flex', className)}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              'justify-start text-left font-normal h-9 px-3',
              !hasValue && 'text-muted-foreground',
              hasValue && 'pr-9 coarse:pr-11',
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            <span className="truncate text-sm">{displayText}</span>
          </Button>
        </PopoverTrigger>
        {hasValue && (
          <button
            type="button"
            onClick={handleClear}
            aria-label={t('dateRange.clear', 'Zeitraum zurücksetzen')}
            className="absolute inset-y-0 right-0 flex touch-manipulation items-center justify-center px-2.5 text-muted-foreground opacity-50 hover:opacity-100 focus-visible:opacity-100 coarse:min-w-11"
          >
            <XIcon className="h-4 w-4" aria-hidden="true" />
          </button>
        )}
      </div>
      <PopoverContent className="w-auto p-0" align="start">
        {shortcuts && (
          <div className="flex flex-wrap gap-1 border-b p-2">
            <ShortcutButton
              label={t('dateShortcuts.today', 'Heute')}
              onClick={() => handleShortcut(getToday)}
            />
            <ShortcutButton
              label={t('dateShortcuts.last7days', 'Letzte 7 Tage')}
              onClick={() => handleShortcut(getLast7Days)}
            />
            <ShortcutButton
              label={t('dateShortcuts.last30days', 'Letzte 30 Tage')}
              onClick={() => handleShortcut(getLast30Days)}
            />
            <ShortcutButton
              label={t('dateShortcuts.thisMonth', 'Dieser Monat')}
              onClick={() => handleShortcut(getThisMonth)}
            />
          </div>
        )}
        <Calendar
          mode="range"
          selected={from || to ? { from, to } : undefined}
          onSelect={handleSelect}
          numberOfMonths={2}
        />
      </PopoverContent>
    </Popover>
  );
}

function ShortcutButton({ label, onClick }: { label: string; onClick: () => void }) {
  // Spec 514: coarse:min-h-10 kommt bereits aus der sm-cva (h-7 kollidiert nicht mit
  // min-h) — hier zusaetzlich explizit, damit die Touch-Absicht am Einsatzort lesbar
  // ist und ein spaeterer cva-Umbau die Shortcuts nicht stillschweigend schrumpft.
  return (
    <Button variant="ghost" size="sm" className="h-7 text-xs coarse:min-h-10" onClick={onClick}>
      {label}
    </Button>
  );
}

// --- Shortcut-Generatoren ---

function getToday(): DateRange {
  const today = new Date();
  return { from: today, to: today };
}

function getLast7Days(): DateRange {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 6);
  return { from, to };
}

function getLast30Days(): DateRange {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 29);
  return { from, to };
}

function getThisMonth(): DateRange {
  const now = new Date();
  const from = new Date(now.getFullYear(), now.getMonth(), 1);
  return { from, to: now };
}
